/**
 * Quick-Open — optional "skip unlock on app open" feature.
 *
 * Encrypts the user's session passphrase with a device-bound key derived from
 * stable browser/OS signals. On cold-start (or background-resume), if the
 * feature is enabled, we attempt to recover the passphrase locally and call
 * login() automatically — the user goes straight into the app without an
 * unlock screen.
 *
 * Crash-safety guarantees:
 *   - Enable is TRANSACTIONAL: pending marker → encrypt → round-trip decrypt
 *     → only then mark enabled. Any failure purges everything.
 *   - Startup auto-purges any incomplete/corrupt state and falls back to manual
 *     unlock — never deadlocks the boot.
 *   - Repeated auto-unlock failures (3x) auto-disable the feature so the user
 *     can never be permanently bricked into the shield-icon hang.
 */
import nacl from 'tweetnacl';
import { encodeBase64, decodeBase64 } from 'tweetnacl-util';
import { safeSetItem } from '@/lib/safeStorage';

const ENABLED_KEY = 'aphenis_quickopen_enabled';
const BLOB_KEY = 'aphenis_quickopen_blob';
const PENDING_ENABLE_KEY = 'aphenis_quickopen_enable_pending';
const FAILURE_COUNT_KEY = 'aphenis_quickopen_failure_count';
const CHECKPOINT_AT_KEY = 'aphenis_quickopen_checkpoint_at';
const CHECKPOINT_PENDING_KEY = 'aphenis_quickopen_checkpoint_pending';
const CHECKPOINT_LAST_SEEN_KEY = 'aphenis_quickopen_checkpoint_last_seen';

const MAX_FAILURES_BEFORE_AUTO_DISABLE = 3;
const DAY_MS = 24 * 60 * 60 * 1000;
const MIN_INTERVAL_MS = 3 * DAY_MS;
const MAX_INTERVAL_MS = 60 * DAY_MS;

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

/** Lightweight, instant device key — based on stable browser/OS signals only.
 *  Never blocks or makes network/audio calls. Pure synchronous string compose
 *  + one SHA-256. Safe in any sandboxed iframe / restrictive WebView. */
async function deriveDeviceKey(): Promise<Uint8Array> {
  const sig = [
    navigator.userAgent || '',
    navigator.language || '',
    navigator.platform || '',
    navigator.hardwareConcurrency || 0,
    (navigator as any).deviceMemory || 0,
    window.screen?.width || 0,
    window.screen?.height || 0,
    window.screen?.colorDepth || 0,
    Intl.DateTimeFormat().resolvedOptions().timeZone || '',
  ].join('|');
  const material = textEncoder.encode(`aphenis-quickopen-lite-v1|${sig}`);
  const hashBuf = await crypto.subtle.digest('SHA-256', material);
  return new Uint8Array(hashBuf).slice(0, nacl.secretbox.keyLength);
}

export function isQuickOpenEnabled(): boolean {
  try { return localStorage.getItem(ENABLED_KEY) === 'true'; } catch { return false; }
}

export function hasQuickOpenBlob(): boolean {
  try { return !!localStorage.getItem(BLOB_KEY); } catch { return false; }
}

// ─────────────────────────── Failure tracking ───────────────────────────

function getFailureCount(): number {
  try {
    const raw = localStorage.getItem(FAILURE_COUNT_KEY);
    const n = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(n) ? n : 0;
  } catch { return 0; }
}

function incrementFailureCount(): number {
  const next = getFailureCount() + 1;
  try { safeSetItem(FAILURE_COUNT_KEY, String(next)); } catch {}
  return next;
}

function resetFailureCount(): void {
  try { localStorage.removeItem(FAILURE_COUNT_KEY); } catch {}
}

/**
 * Startup self-healing: detects half-written/incomplete quick-open state and
 * purges it so the boot never deadlocks. Call this BEFORE any tryQuickOpenUnlock.
 *
 * Returns `true` if the state was repaired (caller should treat quick-open as
 * disabled for this session).
 */
export function selfHealQuickOpen(): boolean {
  try {
    // Case 1: Pending-enable marker left over from a crashed enable attempt.
    if (localStorage.getItem(PENDING_ENABLE_KEY) === '1') {
      console.warn('[QuickOpen] Found pending-enable marker — purging incomplete state');
      purgeQuickOpen();
      return true;
    }
    // Case 2: Marked enabled but blob is missing → corrupt
    if (isQuickOpenEnabled() && !hasQuickOpenBlob()) {
      console.warn('[QuickOpen] Enabled but blob missing — purging');
      purgeQuickOpen();
      return true;
    }
    // Case 3: Too many consecutive auto-unlock failures → auto-disable
    if (getFailureCount() >= MAX_FAILURES_BEFORE_AUTO_DISABLE) {
      console.warn(`[QuickOpen] ${MAX_FAILURES_BEFORE_AUTO_DISABLE}+ failures — auto-disabling for safety`);
      purgeQuickOpen();
      return true;
    }
    return false;
  } catch (e) {
    console.warn('[QuickOpen] selfHeal threw — purging defensively:', e);
    try { purgeQuickOpen(); } catch {}
    return true;
  }
}

// ─────────────────────────── Checkpoint helpers ───────────────────────────

/** Cryptographically random integer in [min, max] inclusive.
 *  Uses two Uint32 values to build a 53-bit random (safe for JS integers)
 *  so ranges exceeding 2^32 never cause an infinite rejection loop. */
function secureRandomInt(min: number, max: number): number {
  const range = max - min + 1;
  // Defensive: if range is non-positive or non-finite, return min
  if (!Number.isFinite(range) || range <= 0) return min;
  // For small ranges that fit in Uint32 use classic rejection sampling
  if (range <= 0xFFFFFFFF) {
    const buf = new Uint32Array(1);
    const limit = Math.floor(0xFFFFFFFF / range) * range;
    // Safety cap: never loop more than 100 times (astronomically unlikely to need >2)
    for (let i = 0; i < 100; i++) {
      crypto.getRandomValues(buf);
      if (buf[0] < limit) return min + (buf[0] % range);
    }
    // Fallback if somehow 100 iterations weren't enough
    return min + (buf[0] % range);
  }
  // Large range (>2^32): build a 53-bit random from two Uint32 values
  const buf2 = new Uint32Array(2);
  crypto.getRandomValues(buf2);
  const r53 = (buf2[0] * 2097152) + (buf2[1] >>> 11); // 2^21 * hi + lo>>11 = 53 bits
  return min + Math.floor((r53 / 9007199254740992) * range); // r53 / 2^53 * range
}

/** Pick a fresh random deadline in [3d, 60d] and persist it. */
export function scheduleNextCheckpoint(): number {
  const now = Date.now();
  let delta: number;
  try {
    delta = secureRandomInt(MIN_INTERVAL_MS, MAX_INTERVAL_MS);
  } catch (e) {
    console.warn('[QuickOpen] secureRandomInt threw — using 7-day fallback:', e);
    delta = 7 * DAY_MS;
  }
  // Sanity clamp
  if (!Number.isFinite(delta) || delta < MIN_INTERVAL_MS) delta = 7 * DAY_MS;
  const at = now + delta;
  try {
    safeSetItem(CHECKPOINT_AT_KEY, String(at));
    safeSetItem(CHECKPOINT_LAST_SEEN_KEY, String(now));
    localStorage.removeItem(CHECKPOINT_PENDING_KEY);
  } catch {}
  return at;
}

export function getCheckpointAt(): number | null {
  try {
    const raw = localStorage.getItem(CHECKPOINT_AT_KEY);
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  } catch { return null; }
}

export function getDaysUntilCheckpoint(): number | null {
  const at = getCheckpointAt();
  if (at == null) return null;
  const delta = at - Date.now();
  if (delta <= 0) return 0;
  return Math.max(0, Math.ceil(delta / DAY_MS));
}

export function isCheckpointDue(): boolean {
  try {
    if (localStorage.getItem(CHECKPOINT_PENDING_KEY) === '1') return true;
    const at = getCheckpointAt();
    if (at == null) return false;
    const now = Date.now();
    if (now >= at) return true;
    const lastSeen = parseInt(localStorage.getItem(CHECKPOINT_LAST_SEEN_KEY) || '0', 10);
    if (Number.isFinite(lastSeen) && lastSeen > 0 && now < lastSeen - DAY_MS) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

function markCheckpointPending(): void {
  try { safeSetItem(CHECKPOINT_PENDING_KEY, '1'); } catch {}
}

export function markCheckpointSatisfied(): void {
  try { localStorage.removeItem(CHECKPOINT_PENDING_KEY); } catch {}
  scheduleNextCheckpoint();
}

// ─────────────────────────── Enable / disable ───────────────────────────

/** Yield a macrotask so the UI thread can paint between heavy stages. */
function yieldToUi(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => setTimeout(resolve, 0));
    } else {
      setTimeout(resolve, 0);
    }
  });
}

/**
 * Encrypt and store the passphrase. TRANSACTIONAL — performs a round-trip
 * decryption to validate before enabling. Auto-rolls back on any failure.
 *
 * STAGED: yields to the UI thread between every heavy step so that even on
 * slow devices the app stays responsive throughout the entire enable flow.
 */
export async function enableQuickOpen(passphrase: string): Promise<boolean> {
  if (!passphrase) return false;

  // Defensive: clear any leftover state from a previous enable attempt
  try { localStorage.removeItem(PENDING_ENABLE_KEY); } catch {}

  try {
    console.log('[QuickOpen] enable:start');

    // 1) Mark pending so a crash here is detected on next boot
    safeSetItem(PENDING_ENABLE_KEY, '1');
    await yieldToUi();

    const wasEnabled = isQuickOpenEnabled();

    // 2) Derive key (involves SubtleCrypto digest — async)
    const key = await deriveDeviceKey();
    console.log('[QuickOpen] enable:key-derived');
    await yieldToUi();

    // 3) Encrypt
    const nonce = nacl.randomBytes(nacl.secretbox.nonceLength);
    const ciphertext = nacl.secretbox(textEncoder.encode(passphrase), nonce, key);
    if (!ciphertext) throw new Error('encryption produced no ciphertext');
    console.log('[QuickOpen] enable:encrypted');
    await yieldToUi();

    // 4) Round-trip validation — decrypt the blob we just produced
    const decryptedTest = nacl.secretbox.open(ciphertext, nonce, key);
    if (!decryptedTest || textDecoder.decode(decryptedTest) !== passphrase) {
      throw new Error('round-trip validation failed');
    }
    console.log('[QuickOpen] enable:roundtrip-ok');
    await yieldToUi();

    // 5) Build blob + persist
    const blob = JSON.stringify({
      v: 1,
      n: encodeBase64(nonce),
      c: encodeBase64(ciphertext),
    });
    safeSetItem(BLOB_KEY, blob);
    await yieldToUi();
    safeSetItem(ENABLED_KEY, 'true');

    if (!wasEnabled || getCheckpointAt() == null) {
      scheduleNextCheckpoint();
    }

    // 6) Clear pending marker + reset failure counter
    try { localStorage.removeItem(PENDING_ENABLE_KEY); } catch {}
    resetFailureCount();
    console.log('[QuickOpen] enable:done');
    return true;
  } catch (e) {
    console.warn('[QuickOpen] Failed to enable — rolling back:', e);
    try {
      localStorage.removeItem(BLOB_KEY);
      localStorage.removeItem(ENABLED_KEY);
      localStorage.removeItem(PENDING_ENABLE_KEY);
    } catch {}
    return false;
  }
}

export function disableQuickOpen(): void {
  try { localStorage.removeItem(BLOB_KEY); } catch {}
  try { localStorage.setItem(ENABLED_KEY, 'false'); } catch {}
  try { localStorage.removeItem(PENDING_ENABLE_KEY); } catch {}
  try { localStorage.removeItem(FAILURE_COUNT_KEY); } catch {}
  try { localStorage.removeItem(CHECKPOINT_AT_KEY); } catch {}
  try { localStorage.removeItem(CHECKPOINT_PENDING_KEY); } catch {}
  try { localStorage.removeItem(CHECKPOINT_LAST_SEEN_KEY); } catch {}
}

export function purgeQuickOpen(): void {
  try { localStorage.removeItem(BLOB_KEY); } catch {}
  try { localStorage.removeItem(ENABLED_KEY); } catch {}
  try { localStorage.removeItem(PENDING_ENABLE_KEY); } catch {}
  try { localStorage.removeItem(FAILURE_COUNT_KEY); } catch {}
  try { localStorage.removeItem(CHECKPOINT_AT_KEY); } catch {}
  try { localStorage.removeItem(CHECKPOINT_PENDING_KEY); } catch {}
  try { localStorage.removeItem(CHECKPOINT_LAST_SEEN_KEY); } catch {}
}

/**
 * Attempt to recover the passphrase. Returns null on any failure. Caller falls
 * back to the manual unlock screen silently. Tracks consecutive failures so the
 * feature auto-disables after 3 fails (preventing brick-loops).
 */
export async function tryQuickOpenUnlock(): Promise<string | null> {
  try {
    if (!isQuickOpenEnabled()) return null;
    if (isCheckpointDue()) {
      markCheckpointPending();
      return null;
    }
    const raw = localStorage.getItem(BLOB_KEY);
    if (!raw) {
      incrementFailureCount();
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed?.n || !parsed?.c) {
      incrementFailureCount();
      return null;
    }
    const key = await deriveDeviceKey();
    const nonce = decodeBase64(parsed.n);
    const ciphertext = decodeBase64(parsed.c);
    const plain = nacl.secretbox.open(ciphertext, nonce, key);
    if (!plain) {
      const fc = incrementFailureCount();
      console.warn(`[QuickOpen] Decryption failed (${fc}/${MAX_FAILURES_BEFORE_AUTO_DISABLE}) — fingerprint may have shifted`);
      return null;
    }
    try { safeSetItem(CHECKPOINT_LAST_SEEN_KEY, String(Date.now())); } catch {}
    resetFailureCount();
    return textDecoder.decode(plain);
  } catch (e) {
    console.warn('[QuickOpen] Unlock attempt threw:', e);
    incrementFailureCount();
    return null;
  }
}
